/**
 * @fileoverview Own selected portal-result inspection shared by Inbox and matter views. Backend context governs sensitive reads, attachments, revocation, and current review revision. `BankingDetails.js` owns banking actions and `IdentityImages.js` owns image evidence. Displayed submission, verified identity, accepted bank version, and notification acknowledgement remain separate facts.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import { AppButton, AppIcon } from '../shared';
import BankingDetails from './BankingDetails';
import IdentityImages from './IdentityImages';
import './VerificationTiers.css';
import './PaymentReviewPanel.css';
import '../Admin/PortalNotificationSettings.css';
import './PortalResult.css';



export default function PortalResult({ reviewId: suppliedId, matterNumber, clientId, embedded = false, onChanged }) {
  const params = useParams();
  const reviewId = suppliedId || params.reviewId;
  const contextQuery = matterNumber && clientId ? `?matter_number=${encodeURIComponent(matterNumber)}&client_id=${encodeURIComponent(clientId)}` : '';
  const contextKey = `${reviewId}${contextQuery}`;
  const [targets, setTargets] = useState(null);
  const [target, setTarget] = useState('');
  const [attachedMessage, setAttachedMessage] = useState('');
  const [result, setResult] = useState(null);
  const [loadedContext, setLoadedContext] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  

  async function revoke() {
    setBusy(true); setError('');
    const current = generation.current;
    try { await api.post(`/payment-instructions/${encodeURIComponent(reviewId)}/revoke`, {}); if (generation.current === current) setResult({ ...result, status: 'revoked', can_reveal: false, can_revoke: false }); }
    catch { if (generation.current === current) { setResult({ ...result, status:'revoking', can_reveal:false }); setError('Revocation could not be confirmed. Please retry.'); } }
    finally { if (generation.current === current) setBusy(false); }
  }
  useEffect(

  () => {
    let active = true;
    generation.current += 1;
    setResult(null); setError(''); setBusy(false); setTargets(null); setTarget(''); setAttachedMessage('');
    api.get(`/payment-instructions/${encodeURIComponent(reviewId)}${contextQuery}`).then(({ data }) => {
      if (active) { setResult(data); setLoadedContext(contextKey); }
    }).catch(() => { if (active) setError('Results could not be loaded, or you do not have access.'); });
    return () => { active = false; generation.current += 1; };
  }, [reviewId, contextQuery, contextKey, refresh]);
  

  async function chooseAttachment() {
    const current = generation.current;
    setBusy(true); setError('');
    try { const { data } = await api.get(`/payment-instructions/${encodeURIComponent(reviewId)}/attachments`); if (current === generation.current) setTargets(data.targets); }
    catch (e) { if (current === generation.current) setError(e.response?.data?.error || 'Could not load other matters.'); }
    finally { if (current === generation.current) setBusy(false); }
  }
  

  async function attach(event) {
    event.preventDefault();
    const current = generation.current;
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/payment-instructions/${encodeURIComponent(reviewId)}/attachments`, {matter_number:target,client_id:result.client_id,revision:result.revision});
      if (current === generation.current) { setResult({...result,revision:data.revision}); setAttachedMessage(`Attached to ${target}.`); setTargets(null); setTarget(''); onChanged?.(); }
    } catch (e) { if (current === generation.current) setError(e.response?.data?.error || 'Could not attach instructions.'); }
    finally { if (current === generation.current) setBusy(false); }
  }
  if (error && !result) return <p role="alert">{error}</p>;
  if (!result || loadedContext !== contextKey) return <p role="status">Loading portal results…</p>;
  const submission = result.submission;
  const verifiedName = submission?.identity?.verified_name || '';
  const normalizeName = name => name.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  const namesDiffer = verifiedName && normalizeName(verifiedName) !== normalizeName(result.expected_identity.name);
  return <section className={`payment-review portal-result${embedded ? " portal-result--embedded" : ""}`} aria-label="Client portal results">
    {!embedded ? <h1>Client portal results</h1> : null}
    {error ? <p role="alert">{error}</p> : null}
    <header className="portal-result__context">
      <div><span className="portal-result__identity-label">Client</span><h2>{result.expected_identity.name}</h2><p>Matter {matterNumber || result.matter.number}</p></div>
      {submission ? <div className="portal-result__stripe-name"><span className="portal-result__identity-label">Stripe ID</span><strong>{verifiedName}</strong>
        {namesDiffer ? <span className="portal-result__name-difference"><AppIcon icon="pi pi-exclamation-triangle" aria-hidden="true" /> Names differ</span> : null}</div> : null}
      {result.test_mode ? <span className="portal-result__sandbox">Sandbox · test results</span> : null}
    </header>
    {result.attached ? <p>Attached from matter {result.matter.number} · original client confirmation retained</p> : null}
    {result.status === 'revoked' ? <p role="status">Request revoked</p> : null}
    {result.status === 'revoking' ? <p role="status">Revocation pending. Bank details are locked; the portal connection will retry.</p> : null}
    {result.status === 'superseded' ? <div className="portal-result__superseded" role="status">
      <strong><AppIcon icon="pi pi-ban" aria-hidden="true" /> Superseded — do not use these instructions</strong>
      {result.replacement_review_id ? <a href={`/case_details/${encodeURIComponent(result.matter.number)}?tab=payment-instructions&payment_result=${encodeURIComponent(result.replacement_review_id)}&client=${result.client_id}`}>Open current request <AppIcon icon="pi pi-arrow-right" aria-hidden="true" /></a> : null}
    </div> : null}
    {result.can_revoke ? <AppButton disabled={busy} onClick={revoke}>Revoke request</AppButton> : null}
    {!submission ? <p>Awaiting client submission.</p> : <>
      <BankingDetails reviewId={reviewId} result={result} matterNumber={matterNumber} clientId={clientId}
        onReload={() => setRefresh(value => value + 1)}
        onUpdated={data => { setResult(current => ({ ...current, ...data })); onChanged?.(); }} />
      {(result.workflow?.enabled === false ? (result.can_view_identity_images ?? result.can_reveal)
        : !result.can_reveal && result.can_view_identity_images)
        ? <IdentityImages reviewId={reviewId} matterNumber={matterNumber} clientId={clientId} canView /> : null}
      <section className="payment-review__destination">
        <h3>Verification record</h3>
        <p><strong>Stripe ID and selfie passed</strong></p>
        {result.verification?.recorded_by ? <p>Recorded by {result.verification.recorded_by} · {new Date(result.verification.recorded_at).toLocaleString()}</p> : null}
        {result.verification?.history?.length ? <details className="verification-record">
          <summary>Verification history</summary>
          <ol>{result.verification.history.map((entry, index) => <li key={index}>
            <strong>{entry.label}</strong> · {entry.recorded_by} · {new Date(entry.recorded_at).toLocaleString()}
            <p>{entry.note}</p>
          </li>)}</ol>
        </details> : null}
      </section>
      {submission.contact_phones?.length ? <section className="payment-review__destination" aria-label="Client-provided phone numbers">
        <h3>Client-provided phone numbers</h3>
        {submission.contact_phones.map((phone, index) => <p key={index}>{phone}</p>)}
      </section> : null}
      {result.can_attach ? <section aria-label="Attach banking instructions">
        <AppButton disabled={busy} icon="pi pi-link" variant="secondary" onClick={chooseAttachment}>Attach to another matter</AppButton>
        {targets ? targets.length ? <form onSubmit={attach}>
          <label htmlFor="banking-target-matter">Another matter for {result.expected_identity.name}</label>
          <select id="banking-target-matter" value={target} onChange={e => setTarget(e.target.value)} required disabled={busy}>
            <option value="">Choose matter</option>
            {targets.map(t => <option key={t.matter_number} value={t.matter_number}>{t.matter_number}</option>)}
          </select>
          <AppButton type="submit" disabled={busy || !target}>Attach banking instructions</AppButton>
        </form> : <p>No other eligible matters for this client.</p> : null}
        {attachedMessage ? <p role="status">{attachedMessage}</p> : null}
      </section> : null}
      {result.test_mode && result.notification_preview ? <section className="payment-review__destination" aria-label="Slack notification preview">
        <h3>Slack notification preview</h3>
        {result.notification_preview.blocks.filter((block) => block.type === 'section').map((block, i) => <p style={{ whiteSpace: 'pre-line' }} key={i}>{block.text.text}</p>)}
        <p>Open in case details ↗</p>
      </section> : null}
    </>}
  </section>;
}
